import { traceUrl } from '../components/agent-common/TraceLink';
import React from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Link, TextField, Typography } from '@mui/material';
import api from '../apis';
import store from '../stores/useVoyagerStore';
import { notedOccurrences, notedSessions, noteFailure, supportContext } from './context';
import { SupportRef } from './SupportRef';
import { captureScreen, EXCLUDE_FROM_SCREENSHOT } from './capture';

type Report = { id: string | null; link: string; shot: string | null; error: string };
type Sent = { shared_until: number | null; notified: { email: boolean; slack: boolean }; screenshot: boolean };

export function SupportButton() {
  const [report, setReport] = React.useState<Report | null>(null), [busy, setBusy] = React.useState(false);
  const open = async () => {
    const generation = store.getState().threadsGeneration;
    setBusy(true);
    try {
      // the page as it is now, before the dialog covers it; it stays in the browser unless the person sends it
      const shot = await captureScreen();
      if (generation !== store.getState().threadsGeneration) return;
      try {
        // what is mounted on screen right now, most recently mounted first (#472): an answer's TraceLink is its
        // turn's registration, so a chat left behind is no longer sent
        const sessions = notedSessions().reverse().slice(0, 100);
        const occurrences = notedOccurrences().reverse().slice(0, 100);
        const { data } = await api.post('/api/support', { ...supportContext(), page: window.location.pathname, sessions, occurrences });
        if (generation !== store.getState().threadsGeneration) return;
        setReport({ id: data.id, link: `${window.location.origin}/support/${data.id}`, shot, error: '' });
      } catch {
        if (generation !== store.getState().threadsGeneration) return;
        setReport({ id: null, link: '', shot: null, error: `Could not save diagnostics. Support reference: ${supportContext().page_id}. Share this reference and the time of the problem.` });
      }
    } finally { setBusy(false); }
  };
  return <>
    <Button size="small" variant="outlined" onClick={open} disabled={busy} {...{ [EXCLUDE_FROM_SCREENSHOT]: '' }}
      sx={{ position: 'fixed', right: 12, bottom: 8, zIndex: 1400, bgcolor: 'background.paper' }}>Report a problem</Button>
    {report && <ReportDialog report={report} onClose={() => setReport(null)} />}
  </>;
}

function ReportDialog({ report, onClose }: { report: Report; onClose: () => void }) {
  const [include, setInclude] = React.useState(!!report.shot), [comment, setComment] = React.useState('');
  const [copied, setCopied] = React.useState(''), [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState<Sent | null>(null), [problem, setProblem] = React.useState('');
  const copy = async () => {
    try { await navigator.clipboard.writeText(report.link); setCopied('Copied.'); } catch { setCopied('Select the link to copy it.'); }
  };
  const send = async () => {
    setSending(true); setProblem('');
    try {
      const { data } = await api.post(`/api/support/${encodeURIComponent(report.id!)}/send`,
        { comment: comment.trim(), ...(include && report.shot ? { screenshot: report.shot } : {}) });
      setSent({ shared_until: data.support_access?.expires ?? null, notified: data.notified, screenshot: !!data.submission?.screenshot });
    } catch (e: any) {
      setProblem(e?.response?.status === 409 ? 'This report was already sent.' : 'Could not send. Try again, or copy the link and share it with us.');
    } finally { setSending(false); }
  };
  const withShot = include && !!report.shot;
  return <Dialog open onClose={onClose} fullWidth maxWidth="sm" data-testid="support-report-dialog">
    <DialogTitle>{sent ? 'Sent to Stroma support' : 'Report a problem'}</DialogTitle>
    <DialogContent>
      {report.error && <Typography>{report.error}</Typography>}
      {report.id && <>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Support link (saved for 7 days; opens only in this account and workspace)</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Link href={report.link} sx={{ overflowWrap: 'anywhere', flex: 1 }}>{report.link}</Link>
          <Button size="small" onClick={copy}>Copy link</Button>
        </Box>
        {copied && <Typography variant="caption">{copied}</Typography>}
      </>}
      {report.id && sent && <Box data-testid="support-report-sent">
        <Typography>We have your report{sent.screenshot ? ' and screenshot' : ''}. Reference {report.id.slice(0, 8)}.</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Shared with Stroma support{sent.shared_until ? ` until ${new Date(sent.shared_until * 1000).toLocaleString()}` : ''}.
          Open the link to stop sharing at any time; stopping deletes the screenshot.
        </Typography>
      </Box>}
      {report.id && !sent && <>
        {report.shot
          ? <Box component="img" src={report.shot} alt="Screenshot of this page" data-testid="support-report-shot"
              sx={{ display: 'block', maxWidth: '100%', maxHeight: 200, border: 1, borderColor: 'divider', borderRadius: 1, opacity: include ? 1 : 0.35 }} />
          : <Typography variant="body2" sx={{ color: 'text.secondary' }}>A screenshot could not be taken on this page.</Typography>}
        <FormControlLabel disabled={!report.shot} label="Include screenshot"
          control={<Checkbox checked={withShot} onChange={(e) => setInclude(e.target.checked)} />} />
        <TextField label="What went wrong? (optional)" value={comment} onChange={(e) => setComment(e.target.value)}
          multiline minRows={3} fullWidth inputProps={{ maxLength: 4000 }} sx={{ mt: 1 }} />
        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }} data-testid="support-report-disclosure">
          {withShot
            ? 'Stroma support will see this screenshot, including any numbers on it, plus this page\'s IDs, outcomes and timings. Your comment is sent to our support team by email and Slack.'
            : 'Stroma support will see this page\'s IDs, outcomes and timings — not your agent traces or your data. Your comment is sent to our support team by email and Slack.'}
        </Typography>
        {problem && <Alert severity="error" sx={{ mt: 1 }}>{problem}</Alert>}
      </>}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{sent || !report.id ? 'Close' : 'Cancel'}</Button>
      {report.id && !sent && <Button variant="contained" onClick={send} disabled={sending}>Send</Button>}
    </DialogActions>
  </Dialog>;
}
export class SupportBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { noteFailure('render'); }
  render() { return this.state.failed ? <Box sx={{ p: 3 }}><Alert severity="error">This page could not be displayed. Use Report a problem so we can investigate.</Alert><Button onClick={() => window.location.reload()}>Reload</Button></Box> : this.props.children; }
}
// Sharing one reference with Voyager support (#472 item 3): the creator's explicit, time-limited, revocable
// grant — staff then read this snapshot's metadata (ids, outcomes, timings), never traces or data.
function SupportAccess({ reference, access, onChange }: { reference: string; access: any; onChange: (a: any) => void }) {
  const [busy, setBusy] = React.useState(false), [problem, setProblem] = React.useState('');
  if (!access?.can_manage) return null;
  if (!access.shared && access.creator === false) return null;      // only its creator shares a reference
  const act = async (share: boolean) => {
    setBusy(true); setProblem('');
    const url = `/api/support/${encodeURIComponent(reference)}/grant`;
    try {
      const { data } = share ? await api.post(url) : await api.delete(url);
      onChange(data.support_access);
    } catch {
      // the server's state decides, not the request: a stop can land and its audit write still fail
      try {
        const { data } = await api.get(url);
        onChange(data.support_access);
        if (share ? data.support_access.shared : !data.support_access.shared) return;
      } catch { /* the state could not be read either */ }
      setProblem(share ? 'Could not share with Stroma support. Try again.' : 'Could not stop sharing. Try again.');
    } finally { setBusy(false); }
  };
  return <Box sx={{ my: 2 }} data-testid="support-access">
    {access.shared
      ? <Alert severity="info" action={<Button color="inherit" size="small" disabled={busy} onClick={() => act(false)}>Stop sharing</Button>}>
          Shared with Stroma support until {new Date(access.expires * 1000).toLocaleString()}. They see this snapshot&apos;s IDs,
          outcomes and timings — not your agent traces or your data. Every view is in your workspace&apos;s audit log.
        </Alert>
      : <Alert severity="info" action={<Button color="inherit" size="small" disabled={busy} onClick={() => act(true)}>Share with Stroma support</Button>}>
          Stroma support cannot see this. Share it for {access.hours} hours to let them investigate: they will see its IDs,
          outcomes and timings — not your agent traces or your data. You or a workspace admin can stop sharing at any time.
        </Alert>}
    {problem && <Alert severity="error">{problem}</Alert>}
  </Box>;
}

// the image needs the signed-in request (a bare <img src> carries no auth), so it is fetched and shown as a blob
function SupportScreenshot({ path }: { path: string }) {
  const [src, setSrc] = React.useState(''), [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    const c = new AbortController();
    let url = '';
    api.get(path, { responseType: 'blob', signal: c.signal })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => { if (!c.signal.aborted) setFailed(true); });
    return () => { c.abort(); if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (failed) return <Typography variant="body2">The screenshot is no longer available.</Typography>;
  return src ? <Box component="img" src={src} alt="Screenshot sent with this report" data-testid="support-screenshot"
    sx={{ display: 'block', maxWidth: '100%', border: 1, borderColor: 'divider', borderRadius: 1 }} /> : null;
}

export function SupportPage({ reference }: { reference: string }) {
  const [data, setData] = React.useState<any>(null), [error, setError] = React.useState('');
  React.useEffect(() => {
    const c = new AbortController();
    const get = (path: string) => api.get(path, { signal: c.signal });
    // the creator's own view first; a Voyager support account reads what was shared with it instead
    get(`/api/support/${encodeURIComponent(reference)}`)
      .catch((e) => { if (c.signal.aborted) throw e; return get(`/api/support/staff/${encodeURIComponent(reference)}`); })
      // an admin of the workspace sees only whether it is shared, to stop it — none of the diagnostics
      .catch((e) => { if (c.signal.aborted) throw e; return get(`/api/support/${encodeURIComponent(reference)}/grant`)
        .then((r) => ({ data: { adminOnly: true, support_access: r.data.support_access } })); })
      .then(r => { if (!c.signal.aborted) setData(r.data); })
      .catch(() => { if (!c.signal.aborted) setError('Support reference unavailable in this account/workspace, or expired.'); });
    return () => c.abort();
  }, [reference]);
  const staff = data?.staff_view;
  return <Box sx={{ p: 3, overflow: 'auto', height: '100vh' }}>
    <Typography variant="h5">Support diagnostics</Typography><Button href="/">Home</Button>
    {error && <Alert severity="error">{error}</Alert>}
    {!data && !error && <Typography>Loading diagnostics…</Typography>}
    {data?.adminOnly && <Box data-testid="support-admin-view">
      <Typography sx={{ my: 1 }}>A support reference from your workspace. As a workspace admin you can see whether it is shared
        with Stroma support and stop sharing it; its diagnostics are its creator&apos;s.</Typography>
      <SupportAccess reference={reference} access={data.support_access} onChange={(a) => setData({ ...data, support_access: a })} />
      {!data.support_access.shared && <Typography>Not shared with Stroma support.</Typography>}
    </Box>}
    {data && !data.adminOnly && <>
      {staff && <Alert severity="warning" data-testid="support-staff-view">Stroma support view — shared by the customer until {new Date(staff.shared_until * 1000).toLocaleString()}. Traces are not included; this view is audited.</Alert>}
      <SupportAccess reference={reference} access={data.support_access} onChange={(a) => setData({ ...data, support_access: a })} />
      <Typography>Captured {new Date(data.created * 1000).toLocaleString()} · expires {new Date(data.expires * 1000).toLocaleString()}</Typography>
      <Typography>Browser build: {data.client_build}</Typography>
      {!staff && <Link href={data.page}>Open reported page</Link>}{staff && <Typography>Reported page: {data.page}</Typography>}
      <Typography>Reference: {data.id}</Typography>
      {data.submission && <Box sx={{ mt: 2 }} data-testid="support-submission">
        <Typography variant="h6">Sent to Stroma support</Typography>
        <Typography>{new Date(data.submission.sent_at * 1000).toLocaleString()}</Typography>
        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{data.submission.comment || '(no comment)'}</Box>
        {data.submission.screenshot
          ? <SupportScreenshot path={`/api/support/${staff ? 'staff/' : ''}${encodeURIComponent(reference)}/screenshot`} />
          : <Typography variant="body2">No screenshot (not included, or deleted when sharing stopped).</Typography>}
      </Box>}
      {data.failures.length > 0 && <Alert severity="warning">Browser failures: {data.failures.join(', ')}</Alert>}
      {staff
        ? data.sessions.length > 0 && <Typography data-testid="support-staff-sessions">Trace session IDs (not opened for support): {data.sessions.join(', ')}</Typography>
        : data.sessions.map((s: string) => <Box key={s}><SupportRef session={s} /><Link href={traceUrl({ session: s })}>Agent trace {s}</Link></Box>)}
      <Typography variant="h6" sx={{ mt: 2 }}>Requests</Typography>
      {data.request_ids?.length > 0 && <Typography>Browser-reported failed request IDs: {data.request_ids.join(', ')}</Typography>}
      <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(data.events, null, 2)}</Box>
      {data.jobs?.length > 0 && <Typography variant="h6" sx={{ mt: 2 }}>Background jobs</Typography>}
      {(data.jobs || []).map((j: any) => <Box key={j.job_id} sx={{ mt: 1 }}>
        <Typography>{j.job_kind}{j.ref ? ` · ${j.ref}` : ''} · {j.outcome} · job {j.job_id}{j.parent_job ? ` · started by ${j.parent_job}` : ''}{j.omitted ? ` · ${j.omitted} events omitted` : ''}</Typography>
        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(j.events, null, 2)}</Box>
      </Box>)}
    </>}
  </Box>;
}
